import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { Loader2, Save } from "lucide-react";
import Button from "@/shared/components/button";
import Field from "@/shared/components/field";
import JsonSchemaEditor from "@/shared/components/json_schema_editor";
import { useIntegrationProviders } from "../hooks/useIntegrationProviders";
import type IntegrationProvider from "../models/integrationProvider";
import AuthType from "../valueObjects/authType";
import UserAuthType from "../valueObjects/userAuthType";
import GlassBackground from "@/shared/components/glassBackground";
import BackButton from "@/shared/components/backButton";

type IntegrationFormData = Omit<IntegrationProvider, "id">;

const AUTH_TYPE_OPTIONS = [
  { value: AuthType.Custom, label: "Personalizado" },
  { value: AuthType.Webhook, label: "Webhook" },
  { value: AuthType.Bearer, label: "Bearer Token" },
  { value: AuthType.ApiKey, label: "API Key" },
  { value: AuthType.OAuth2, label: "OAuth2" },
];

const USER_AUTH_TYPE_OPTIONS = [
  { value: UserAuthType.None, label: "Ninguno" },
  { value: UserAuthType.ApiKeyInput, label: "Entrada de API Key" },
  { value: UserAuthType.OAuth2Login, label: "Login OAuth2" },
];

const CATEGORY_OPTIONS = [
  { value: "social", label: "Social" },
  { value: "enterprise", label: "Empresa" },
  { value: "communication", label: "Comunicación" },
  { value: "analytics", label: "Análitica" },
  { value: "payment", label: "Pagos" },
  { value: "other", label: "Otro" },
];

const DEFAULT_FORM: IntegrationFormData = {
  name: "",
  description: "",
  icon: "",
  category: "social",
  authType: AuthType.Custom,
  userAuthType: UserAuthType.None,
  oAuthSettings: "",
  developerConfigSchema: "",
  IsGlobal: false,
};

export default function IntegrationFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, create, update, loading, error } = useIntegrationProviders();

  const isEditing = !!id;

  const [formData, setFormData] = useState<IntegrationFormData>(DEFAULT_FORM);

  useEffect(() => {
    if (isEditing && id) {
      const fetchIntegration = async () => {
        const integration = await get(id);
        if (integration) {
          setFormData({
            name: integration.name,
            description: integration.description || "",
            icon: integration.icon || "",
            category: integration.category,
            authType: integration.authType,
            userAuthType: integration.userAuthType,
            oAuthSettings: integration.oAuthSettings || "",
            developerConfigSchema: integration.developerConfigSchema || "",
            IsGlobal: integration.IsGlobal,
          });
        }
      };
      fetchIntegration();
    }
  }, [id, isEditing, get]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    const isNumeric = name === "authType" || name === "userAuthType";
    setFormData((prev) => ({
      ...prev,
      [name]: isNumeric ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await update(id, formData);
      } else {
        await create(formData);
      }
      navigate("/admin/integrations");
    } catch {
      alert("Error al guardar la integración");
    }
  };

  return (
    <GlassBackground>
      <div className="relative flex-1 overflow-auto p-10">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <BackButton text={"Volver a integraciones"} link={"/admin/integrations"}>
            <h2 className="text-[40px] font-bold tracking-tight text-foreground">
              {isEditing ? "Editar Integración" : "Nueva Integración"}
            </h2>
            <p className="text-foreground/50 text-lg font-medium mt-1">
              {isEditing
                ? "Actualiza la información del proveedor de autenticación."
                : "Configura un nuevo proveedor de autenticación."}
            </p>
          </BackButton>

          {/* Form Card */}
          <div className="bg-white/60 border border-black/5 backdrop-blur-md rounded-4xl p-8 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)]">
            {isEditing && loading && (
              <div className="flex justify-center py-10">
                <Loader2 className="size-8 animate-spin text-accent" />
              </div>
            )}

            {error && (
              <p className="text-red-500 text-sm mb-6">
                Error: {error.message}
              </p>
            )}

            {(!isEditing || !loading) && (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Información básica */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Información Básica
                  </h3>
                  <div className="space-y-4">
                    <Field
                      label="Nombre del proveedor"
                      name="name"
                      value={formData.name}
                      placeholder="Ej: Google OAuth"
                      required
                      onChange={handleChange}
                    />

                    <Field
                      label="Descripción"
                      name="description"
                      value={formData.description ?? ""}
                      placeholder="Describe brevemente esta integración..."
                      onChange={handleChange}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Field
                        label="Icono (URL)"
                        name="icon"
                        value={formData.icon ?? ""}
                        placeholder="https://example.com/icon.png"
                        onChange={handleChange}
                      />

                      <Field
                        label="Categoría"
                        name="category"
                        type="select"
                        value={formData.category}
                        onChange={handleChange}
                        options={CATEGORY_OPTIONS}
                      />
                    </div>
                  </div>
                </div>

                {/* Configuración de autenticación */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Configuración de Autenticación
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Field
                        label="Tipo de autenticación"
                        name="authType"
                        type="select"
                        value={formData.authType}
                        onChange={handleChange}
                        options={AUTH_TYPE_OPTIONS}
                      />

                      <Field
                        label="Tipo de autenticación de usuario"
                        name="userAuthType"
                        type="select"
                        value={formData.userAuthType}
                        onChange={handleChange}
                        options={USER_AUTH_TYPE_OPTIONS}
                      />
                    </div>

                    <JsonSchemaEditor
                      label="Configuración OAuth2"
                      name="oAuthSettings"
                      value={formData.oAuthSettings ?? ""}
                      onChange={(val) =>
                        setFormData((prev) => ({ ...prev, oAuthSettings: val }))
                      }
                      placeholder='{"clientId": "...", "clientSecret": "...", "redirectUri": "..."}'
                      rows={6}
                    />

                    <JsonSchemaEditor
                      label="Esquema de Configuración del Desarrollador"
                      name="developerConfigSchema"
                      value={formData.developerConfigSchema ?? ""}
                      onChange={(val) =>
                        setFormData((prev) => ({
                          ...prev,
                          developerConfigSchema: val,
                        }))
                      }
                      placeholder='{"type": "object", "properties": {"apiKey": {"type": "string"}}}'
                      rows={10}
                    />
                  </div>
                </div>

                {/* Configuración global */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Configuración
                  </h3>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="IsGlobal"
                      checked={formData.IsGlobal}
                      onChange={handleChange}
                      className="w-4 h-4 rounded border-black/20 text-accent focus:ring-2 focus:ring-accent focus:ring-offset-0"
                    />
                    <span className="text-sm font-medium text-foreground">
                      Proveedor global
                    </span>
                  </label>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-6 border-t border-black/5">
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/admin/integrations")}
                  >
                    Cancelar
                  </Button>
                  <Button
                    submit
                    disabled={loading}
                    className="h-11 px-6 rounded-xl bg-accent hover:bg-accent/90 text-white border-none shadow-[0_0_20px_rgba(12,123,254,0.3)]"
                  >
                    {loading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Save className="size-4" />
                    )}
                    {isEditing ? "Guardar cambios" : "Crear integración"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </GlassBackground>
  );
}
